/** Document library and policy acknowledgements — module 10. */

export type LibraryKind = 'DOCUMENT' | 'POLICY' | 'TEMPLATE'
export type LibraryStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
export type FolderAccessMode = 'EVERYONE' | 'ROLES'
export type RetentionBasis = 'PUBLICATION' | 'SUPERSEDED'
export type CampaignStatus = 'OPEN' | 'CLOSED' | 'CANCELLED'
export type AcknowledgementStatus = 'PENDING' | 'ACKNOWLEDGED'

export const LIBRARY_KIND_LABELS: Record<LibraryKind, string> = {
  DOCUMENT: 'Document',
  POLICY: 'Policy',
  TEMPLATE: 'Letter template',
}

export const LIBRARY_STATUS_LABELS: Record<LibraryStatus, string> = {
  DRAFT: 'Not published',
  PUBLISHED: 'Published',
  ARCHIVED: 'Withdrawn',
}

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  OPEN: 'Collecting',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
}

export const ACKNOWLEDGEMENT_STATUS_LABELS: Record<AcknowledgementStatus, string> = {
  PENDING: 'Not answered',
  ACKNOWLEDGED: 'Acknowledged',
}

export interface FolderAccessView {
  id: string
  roleId: string
  roleCode: string | null
  canWrite: boolean
}

export interface DocumentFolderView {
  id: string
  legalEntityId: string | null
  parentId: string | null
  name: string
  description: string | null
  sortOrder: number
  accessMode: string
  access: FolderAccessView[]
  documentCount: number
  accessNote: string
}

export interface RetentionRuleView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  retainMonths: number
  basis: string
  /** REVIEW, always: nothing is destroyed automatically. */
  action: string
  statutoryReference: string | null
  notes: string | null
  note: string
}

export interface RetentionReviewView {
  documentId: string
  title: string
  keepUntil: string | null
  dueForReview: boolean
  note: string
}

export interface LibraryDocumentView {
  id: string
  legalEntityId: string | null
  folderId: string
  folderName: string | null
  kind: string
  title: string
  summary: string | null
  tags: string[]
  ownerEmployeeId: string | null
  ownerName: string | null
  letterTemplateId: string | null
  letterTemplateName: string | null
  documentTypeId: string | null
  documentTypeName: string | null
  reviewDueOn: string | null
  retentionRuleId: string | null
  retentionRuleName: string | null
  status: string
  currentVersionNo: number | null
  publishedAt: string | null
  archiveReason: string | null
  versionCount: number
  alertNote: string
}

export interface LibraryVersionView {
  id: string
  documentId: string
  versionNo: number
  storedFileId: string
  changeSummary: string | null
  effectiveFrom: string | null
  publishedAt: string | null
  publishedBy: string | null
  supersededAt: string | null
  current: boolean
}

export interface PolicyCampaignView {
  id: string
  documentId: string
  documentTitle: string | null
  versionId: string
  versionNo: number
  name: string
  audienceKind: string
  audienceRef: string | null
  dueOn: string | null
  openedAt: string
  closedAt: string | null
  status: string
  /** How many people were asked when this opened. */
  asked: number
  acknowledged: number
  pending: number
  overdue: boolean
  coverageNote: string
}

export interface PolicyAcknowledgementView {
  id: string
  campaignId: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  versionId: string
  versionNo: number
  status: string
  acknowledgedAt: string | null
  note: string | null
}
